import * as UI from "@iyk/music-ui"
import { Data, Match } from "effect"
import * as React from "react"
import { useTrackViewWhenSeen } from "../../analytics/hooks/useTrackViewWhenSeen.ts"
import { useUnlocksAnalytics } from "../../analytics/hooks/useUnlocksAnalytics.tsx"
import type { UiUnlock } from "../../UiUnlock.ts"
import { bodyPadding, Container, Footer, formatDate, Header, LockedUnlock } from "./UnlockCard.tsx"

type LockedProps = {
  content: UiUnlock.Locked.Content
  locale: string
}

const Locked = ({ content, locale }: LockedProps) => (
  <LockedUnlock
    id={content.id}
    title={content.title}
    caption={content.caption}
    world={content.world}
    createdAt={content.createdAt}
    policy={content.policy}
    mediaCount={content.imageCount + content.videoCount}
    locale={locale}
  />
)

type UnlockedProps = {
  content: UiUnlock.Unlocked.Content
  locale: string
  priority?: boolean
}

const Unlocked = ({ content, locale, priority = false }: UnlockedProps) => {
  const { id, title, caption, world, createdAt, media } = content
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [mediaLayout, setMediaLayout] = React.useState<MediaLayout>(new MediaLayout(media, 0))
  const formattedCreatedAt = formatDate(createdAt, locale)
  const [mediaDialogOpen, setMediaDialogOpen] = React.useState(false)
  const { trackView } = useUnlocksAnalytics()
  const viewRef = useTrackViewWhenSeen(id)

  const handleMediaDialogOpenChange = (open: boolean) => {
    setMediaDialogOpen(open)

    if (open) {
      trackView(id)
    }
  }

  React.useLayoutEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return

      const containerWidth = containerRef.current.getBoundingClientRect().width

      setMediaLayout(new MediaLayout(media, containerWidth))
    }

    const resizeObserver = new window.ResizeObserver(handleResize)

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    handleResize()

    return () => {
      resizeObserver.disconnect()
    }
  }, [media])

  return (
    <Container
      ref={viewRef}
      className={UI.cx("transition-opacity duration-300", mediaLayout.isReady ? "opacity-100" : "opacity-0")}
    >
      <UI.MediaDialog.Root slides={media} onOpenChange={handleMediaDialogOpenChange}>
        <Header world={world} title={title} date={formattedCreatedAt} />
        <div ref={containerRef} className="max-w-[100vw] overflow-hidden">
          {MediaType.$match(mediaLayout.media, {
            Single: ({ media }) => (
              <div
                className="flex"
                style={{
                  paddingLeft: bodyPadding.left,
                  paddingRight: bodyPadding.right,
                  ...toCSSProperties("--layout-max-height", "420px")
                }}
              >
                <MediaComponent
                  media={media}
                  index={0}
                  className="max-w-full"
                  triggerClassName="cursor-pointer"
                  mediaDialogOpen={mediaDialogOpen}
                  priority={priority}
                />
              </div>
            ),
            Duo: ({ media, height }) => (
              <div
                className="flex"
                style={{
                  gap: MediaLayout.gap,
                  paddingLeft: bodyPadding.left,
                  paddingRight: bodyPadding.right,
                  ...toCSSProperties("--layout-max-height", `${height}px`)
                }}
              >
                {media.map((m, index) => (
                  <MediaComponent
                    key={index}
                    media={m}
                    index={index}
                    triggerClassName="cursor-pointer"
                    mediaDialogOpen={mediaDialogOpen}
                    priority={priority && index === 0}
                  />
                ))}
              </div>
            ),
            Multiple: ({ media, height }) => (
              <div style={toCSSProperties("--layout-max-height", `${height}px`)}>
                <UI.Carousel.Root
                  slideSize="auto"
                  slideSpacing={6}
                  className="h-full cursor-move"
                  options={{ dragFree: true }}
                >
                  <UI.Carousel.Slides
                    slides={media}
                    style={{
                      paddingLeft: bodyPadding.left,
                      paddingRight: bodyPadding.right
                    }}
                  >
                    {(m, index) => {
                      return (
                        <UI.Carousel.Slide key={index}>
                          <MediaComponent
                            key={index}
                            media={m}
                            index={index}
                            className="flex-none select-none h-[var(--layout-max-height)]"
                            triggerClassName="cursor-grab"
                            mediaDialogOpen={mediaDialogOpen}
                            priority={priority && index === 0}
                          />
                        </UI.Carousel.Slide>
                      )
                    }}
                  </UI.Carousel.Slides>
                </UI.Carousel.Root>
              </div>
            )
          })}
        </div>
        <Footer caption={caption} />
      </UI.MediaDialog.Root>
    </Container>
  )
}

const toCSSProperties = (name: string, value: number | string): React.CSSProperties => ({ [name]: value })

const timelineImageWidth = 800

type MediaComponentProps = {
  media: UiUnlock.Media
  index: number
  className?: string
  triggerClassName?: string
  mediaDialogOpen: boolean
  priority?: boolean
}

const MediaComponent = ({
  media,
  index,
  className,
  triggerClassName,
  mediaDialogOpen,
  priority = false
}: MediaComponentProps) => {
  const dimensions = MediaLayout.toDimensions(media)
  const aspectRatio = dimensions.width / dimensions.height

  const classForMedia = UI.cx(
    "aspect-[var(--aspect-ratio)] w-full max-w-full max-h-[var(--layout-max-height)] flex object-contain select-none",
    className
  )

  return Match.valueTags(media, {
    Image: (m) => (
      <UI.MediaDialog.Trigger
        className={UI.cx(
          UI.classForCard({ rounded: "base", padding: "none" }),
          "flex items-center justify-center",
          triggerClassName
        )}
        style={toCSSProperties("--aspect-ratio", aspectRatio)}
        index={index}
      >
        <img
          src={UI.ImageCdn.src(m.fileUrl, { width: timelineImageWidth })}
          srcSet={UI.ImageCdn.srcSet(m.fileUrl, { width: timelineImageWidth })}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
          className={classForMedia}
          onContextMenu={UI.preventContextMenu}
        />
      </UI.MediaDialog.Trigger>
    ),
    Video: (m) => (
      <div
        className={UI.cx("relative min-w-0 overflow-hidden rounded", classForMedia)}
        style={toCSSProperties("--aspect-ratio", aspectRatio)}
        onContextMenu={UI.preventContextMenu}
      >
        <UI.MediaDialog.Trigger
          className={UI.cx(
            "rounded after:rounded after:content-[''] after:absolute after:inset-0 after:z-10 after:bshadow after:bshadow-grayA-3 after:pointer-events-none",
            "absolute top-0 left-0 size-full pointer-events-auto [&~*]:pointer-events-auto z-10",
            triggerClassName
          )}
          index={index}
        />
        <UI.VideoPlayer
          id={`inline-video-${m.playbackUrl}-${index}`}
          playsInline
          size="fill"
          loop
          kind="minimal"
          src={m.playbackUrl}
          pauseObserver={mediaDialogOpen}
        />
      </div>
    )
  })
}

export type MediaType = Data.TaggedEnum<{
  Single: {
    media: UiUnlock.Media
    height: number
  }
  Duo: {
    media: Array<UiUnlock.Media>
    height: number
  }
  Multiple: {
    media: Array<UiUnlock.Media>
    height: number
  }
}>
const MediaType = Data.taggedEnum<MediaType>()

export type MediaLayoutResult = MediaType

export class MediaLayout {
  static readonly gap = 6
  static readonly maxHeight = 420

  private static defaultDimensions = {
    video: { width: 16, height: 9 },
    image: { width: MediaLayout.maxHeight, height: MediaLayout.maxHeight }
  }

  static toDimensions(media: UiUnlock.Media): { width: number; height: number } {
    return Match.valueTags(media, {
      Image: (m) => ({
        width: m.width ?? this.defaultDimensions.image.width,
        height: m.height ?? this.defaultDimensions.image.height
      }),
      Video: (m) => ({
        width: m.width ?? this.defaultDimensions.video.width,
        height: m.height ?? this.defaultDimensions.video.height
      })
    })
  }

  constructor(
    private readonly allMedia: Array<UiUnlock.Media>,
    private readonly containerWidth: number
  ) {}

  private aspectRatio(media: UiUnlock.Media): number {
    const dimensions = MediaLayout.toDimensions(media)
    return dimensions.width / dimensions.height
  }

  private getDuoHeight(): number {
    if (!this.isReady) return MediaLayout.maxHeight

    const [firstMedia, secondMedia] = this.allMedia
    const firstRatio = this.aspectRatio(firstMedia)
    const secondRatio = this.aspectRatio(secondMedia)

    return (this.containerWidth - MediaLayout.gap) / (firstRatio + secondRatio)
  }

  private getMultipleHeight(): number {
    if (!this.isReady) return MediaLayout.maxHeight

    const maxRatio = Math.max(...this.allMedia.map((m) => this.aspectRatio(m)))
    const targetWidth = (this.containerWidth - bodyPadding.left - bodyPadding.right) * 0.9

    return Math.min(targetWidth / maxRatio, MediaLayout.maxHeight)
  }

  get media() {
    const isSingle = this.allMedia.length === 1
    const isDuo = this.allMedia.length === 2

    if (isSingle) {
      return MediaType.Single({ media: this.allMedia[0], height: MediaLayout.maxHeight })
    }

    if (isDuo) {
      return MediaType.Duo({ media: this.allMedia, height: this.getDuoHeight() })
    }

    return MediaType.Multiple({ media: this.allMedia, height: this.getMultipleHeight() })
  }

  get isReady(): boolean {
    return this.containerWidth > 0
  }
}

export const ContentUnlock = {
  Locked,
  Unlocked
}
